import { FC, useMemo, useState } from "react";
import { Question, Tender, Tendering } from "../../../models/tendering";
import { Badge, Subtitle2, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "@fluentui/react-components";
import HistoryDialog from "../Dialogs/HistoryDialog";
import { User } from "../../../models/users";
import { RoleDTO } from "../../../models/role";
import { useAppContext } from "../../../AppContext";
import { getIndexingPriceChange, getRequestedIndexings } from "../../../utils/tendering-utils";


interface TenderHistoryProps{
  tenderDocument: Tender;
  tenderingDocument: Tendering;
  users: any[];
}

const TenderHistory: FC<TenderHistoryProps> = (props: TenderHistoryProps) => {
  const [questions, setQuestions] = useState<Question[]>(
    props.tenderingDocument.tenderingParts.flatMap(part => part.questions ?? [])
  );
  const [open, setOpen] = useState(false);
  const [selectedChange, setSelectedChange] = useState<any>(null);

  const {user} = useAppContext();

  const changes = useMemo(() => {
    const tender = props.tenderDocument;
    const requestedIndexings = getRequestedIndexings(tender, props.tenderingDocument.indexing || []);
    const indexingChanges = requestedIndexings.flatMap(indexing => {
      const request = tender.indexing?.find(t => Number(t.indexingID) === indexing.indexingID);
      return (indexing.prices || []).flatMap(price => {
        const answer = tender.tenderAnswers?.find(a => a.questionID === price.questionID);
        const change = getIndexingPriceChange(indexing, price.questionID, answer, requestedIndexings);
        if (!change) return [];
        return [{
          isIndexing: true,
          date: indexing.indexingDate,
          indexingName: indexing.indexingName,
          oldAnswer: { value: change.before, comment: "", questionID: price.questionID },
          newAnswer: { value: change.after, comment: `Index ${indexing.index}`, questionID: price.questionID },
          requestedBy: request?.user,
          taskIssuedByUser: request?.user
        }];
      });
    });

    return [...indexingChanges, ...(tender.changes || [])]
      .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [props.tenderDocument, props.tenderingDocument]);

  const getQuestionText = (questionID: number): string => {
    const question = questions.find((q: Question) => q.questionID === questionID);
    return question ? question.text : "Okänd fråga";
  }

  const openModal = (change: any) => {
    setSelectedChange(change);
    setOpen(true);
  }

  const getUserFullName = (identifier: number | string): string => {
    var user;
    if(typeof identifier === 'number'){
      user = props.users.find(u => u.user.userID == identifier);
    }
    else if(typeof identifier === 'string' && identifier !== undefined){
      user = props.users.find(u => u.user.email == identifier.toLowerCase());
    }
    if(user){
      return user.user.firstName + ' ' + user.user.lastName;
    }
    return ''
  }

  return (
    <div>
      <Table size="extra-small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>
              <Subtitle2>Datum</Subtitle2>
            </TableHeaderCell>
            <TableHeaderCell>
              <Subtitle2>Fråga</Subtitle2>
            </TableHeaderCell>
            <TableHeaderCell>
              <Subtitle2>Svarsvärde</Subtitle2>
            </TableHeaderCell>
            <TableHeaderCell>
              <Subtitle2>Kommentar</Subtitle2>
            </TableHeaderCell>
            <TableHeaderCell>
              <Subtitle2>Begärd av</Subtitle2>
            </TableHeaderCell>
            {user.roles.some((r: RoleDTO) => r.roleType === "General") && <TableHeaderCell>
              <Subtitle2>Accepterad av</Subtitle2>
            </TableHeaderCell>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {changes.map((c: any, idx: number) => (
            <TableRow onClick={() => openModal(c)} key={idx}>
              <TableCell>{new Date(c.date).toLocaleDateString("sv-SE")}</TableCell>
              <TableCell>
                {c.isIndexing && <Badge appearance="tint" color="brand" className="mr-2">Prisindexering</Badge>}
                {getQuestionText(c.oldAnswer.questionID)}
              </TableCell>
              <TableCell>{c.newAnswer.value}</TableCell>
              <TableCell>{c.newAnswer.comment}</TableCell>
              <TableCell>{c.isIndexing ? c.taskIssuedByUser : getUserFullName(c.taskIssuedByUser)}</TableCell>
              {user.roles.some((r: RoleDTO) => r.roleType === "General") && <TableCell>{c.isIndexing ? "" : getUserFullName(c.taskApprovedByUser)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {selectedChange && <HistoryDialog change={selectedChange} questionText={getQuestionText(selectedChange?.oldAnswer.questionID)} open={open} onClose={() => {
        setOpen(false);
        setSelectedChange(null);
      }} />}
    </div>
  );
}

export default TenderHistory;